import { useMemo } from "react";
import {
  NotificationEventEnum,
  NotificationChannelEnum,
  type NotificationEvent,
  type NotificationSubscriptions,
  type NotificationChannel,
} from "@jasper/client-models";
import { useMutation, useQuery } from "@apollo/client";
import {
  GetCurrentUserNotificationSubscriptionsDocument,
  SubscribeToNotificationDocument,
  UnsubscribeFromNotificationDocument,
} from "../../gql/generated/graphql";

export const useCurrentUserNotificationSubscriptions = () => {
  const { data, loading, error, refetch } = useQuery(
    GetCurrentUserNotificationSubscriptionsDocument
  );

  const [subscribeToNotification, { loading: isSubscribing }] = useMutation(
    SubscribeToNotificationDocument
  );
  const [unsubscribeFromNotification, { loading: isUnsubscribing }] =
    useMutation(UnsubscribeFromNotificationDocument);

  const notificationSubscriptions = useMemo(
    () =>
      data?.getNotificationSubscriptions.reduce((acc, subscription) => {
        const event = NotificationEventEnum[subscription.event];
        if (!acc[event]) {
          acc[event] = {
            [NotificationChannelEnum.EMAIL]: false,
            [NotificationChannelEnum.SMS]: false,
            [NotificationChannelEnum.PUSH]: false,
          };
        }

        acc[event][subscription.channel] = true;

        return acc;
      }, {} as NotificationSubscriptions),
    [data?.getNotificationSubscriptions]
  );

  return {
    isLoading: loading || isSubscribing || isUnsubscribing,
    error,
    notificationSubscriptions,
    subscribeToNotification: async ({
      event,
      channel,
    }: {
      event: NotificationEvent;
      channel: NotificationChannel;
    }) => {
      await subscribeToNotification({
        variables: { notificationEvent: event, notificationChannel: channel },
      });

      await refetch();
    },
    unsubscribeFromNotification: async ({
      event,
      channel,
    }: {
      event: NotificationEvent;
      channel: NotificationChannel;
    }) => {
      await unsubscribeFromNotification({
        variables: { notificationEvent: event, notificationChannel: channel },
      });
      await refetch();
    },
  };
};
